import {
    inject,
    type ObjectGraph,
} from '@jet/environment/dependencies/object-graph';
import { makeUserComponentsIntent } from '../intents/user-components/UserComponentsIntent';
import { ObjectGraphType } from '../dependencies/object-graph-type';
import type { UserComponentsEntity } from '../models/header';
import type { Opt } from '@jet/environment/types/optional';

export interface ContextualMenuResponse {
    renderContextualMenu: boolean;
    entities: Opt<UserComponentsEntity[]>;
}

/**
 * Utility that queries for contextual menu content and determines if
 * the contextual menu ellipsis button should be rendered or not within.
 * a page header.  In cases where there are only two buttons returned, a
 * different button should be rendered in its place.
 *
 * If there are any library states to be set, pass the entities to the
 * client and set them on the library in the Page component so we
 * don't have to make a duplicate call when CloudButtons mounts.
 *
 * @param {[string]} queryUrl userComponentsAction url
 * @param {ObjkectGraph} objectGraph Object Graph
 * @returns {Promise<ContextualMenuResponse>}
 */
async function shouldRenderContextualMenu(
    queryUrl: string | null,
    objectGraph: ObjectGraph,
): Promise<ContextualMenuResponse> {
    let renderContextualMenu = false;
    let entities = null;

    // queryUrl only exists for Premium users
    if (queryUrl) {
        const intentDispatcher = inject(
            ObjectGraphType.intentDispatcher,
            objectGraph,
        );

        const intentResponse = await intentDispatcher.dispatch(
            makeUserComponentsIntent({
                url: queryUrl,
                actionType: 'fetch',
            }),
            objectGraph,
        );

        renderContextualMenu = intentResponse.secondaryButtonCount > 2 || false;
        entities = intentResponse.entities;
    }

    return { renderContextualMenu, entities };
}

export { shouldRenderContextualMenu };
